import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  QueryClient,
  QueryClientProvider,
  keepPreviousData,
  useQuery,
} from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { toast } from "sonner";
import {
  Loader2,
  Download,
  Search,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  Radio,
  RotateCcw,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { hasAdminAccess } from "@/lib/admin-access";
import { AdminShell } from "@/components/admin/AdminShell";
import { TableSkeleton } from "@/components/admin/Skeletons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  BUCKET_META,
  PAGE_SIZE,
  defaultRanges,
  type PriorityBucket,
  type PriorityRanges,
} from "@/lib/interview-priority";
import {
  getInterviewPriorityExport,
  getInterviewPriorityPage,
} from "@/lib/interview-priority.functions";
import { downloadPriorityWorkbook } from "@/lib/priority-export";
import { buildWhatsAppUrl } from "@/lib/phone";
import { SortControl, useAdminSort } from "@/components/admin/SortControl";

export const Route = createFileRoute("/admin/interview-priority")({
  head: () => ({
    meta: [
      { title: "Interview Priority - WeLivdIt Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: PriorityRoute,
});

const BUCKETS: PriorityBucket[] = ["urgent", "somewhat", "not"];
const ROW_HEIGHT = 44;
const COLS = 9;

function PriorityRoute() {
  // Admin console is client-rendered; a per-mount client keeps caches scoped.
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 30_000, gcTime: 5 * 60_000, refetchOnWindowFocus: false },
        },
      }),
  );
  return (
    <QueryClientProvider client={client}>
      <AdminInterviewPriorityPage />
    </QueryClientProvider>
  );
}

function AdminInterviewPriorityPage() {
  const navigate = useNavigate();
  const [gate, setGate] = useState<"loading" | "ok" | "denied">("loading");

  const [bucket, setBucket] = useState<PriorityBucket>("urgent");
  const [page, setPage] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [ranges, setRanges] = useState<PriorityRanges>(() => defaultRanges());
  const [exporting, setExporting] = useState(false);
  const [exportPct, setExportPct] = useState(0);
  const [live, setLive] = useState(false);
  const [sort, setSort] = useAdminSort("interview-priority");
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        navigate({ to: "/auth", search: { redirect: "/admin/interview-priority" } });
        return;
      }
      setGate((await hasAdminAccess(sess.session)) ? "ok" : "denied");
    })();
  }, [navigate]);

  // 300ms debounce on the search box — one query per pause, never per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const query = useQuery({
    enabled: gate === "ok",
    // Deduplicated per exact filter combination; identical params never refetch.
    queryKey: ["interview-priority", bucket, page, search, status, ranges, sort],
    queryFn: () => getInterviewPriorityPage({ data: { bucket, page, search, status, ranges, sort } }),
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.error) toast.error((query.error as Error).message || "Couldn't load the list.");
  }, [query.error]);

  // Realtime: intake submissions + interview-date changes reshuffle the tabs live.
  useEffect(() => {
    if (gate !== "ok") return;
    const invalidate = () => void query.refetch();
    const channel = supabase
      .channel("interview-priority-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "client_intakes" }, invalidate)
      .on("postgres_changes", { event: "*", schema: "public", table: "payments" }, invalidate)
      .subscribe((s) => setLive(s === "SUBSCRIBED"));
    return () => {
      void supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gate]);

  const rows = query.data?.rows ?? [];
  const total = query.data?.total ?? 0;
  const counts = query.data?.counts ?? { urgent: 0, somewhat: 0, not: 0 };
  const statuses = query.data?.statuses ?? [];
  const showSkeleton = query.isLoading || (query.isFetching && rows.length === 0);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });
  const items = virtualizer.getVirtualItems();

  const runExport = async () => {
    setExporting(true);
    setExportPct(0);
    try {
      const data = await getInterviewPriorityExport({ data: { ranges } });
      await downloadPriorityWorkbook(data, setExportPct, ranges);
      toast.success("Priority report downloaded.");
    } catch (e) {
      toast.error((e as Error).message || "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const setRange = (patch: Partial<PriorityRanges>) => {
    setRanges((r) => ({ ...r, ...patch }));
    setPage(0);
  };

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);
  const description = useMemo(
    () =>
      `Deduped across payments + intake forms. Urgent to ${ranges.urgentEnd}, somewhat ${ranges.somewhatStart} → ${ranges.somewhatEnd}.`,
    [ranges],
  );

  if (gate === "denied")
    return (
      <div className="min-h-[60vh] grid place-items-center px-5">
        <p className="text-muted-foreground">Not authorized.</p>
      </div>
    );

  return (
    <AdminShell
      title="Interview Priority"
      description={description}
      actions={
        <Button onClick={runExport} disabled={exporting || gate !== "ok"} className="gap-2">
          {exporting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Building… {exportPct}%
            </>
          ) : (
            <>
              <Download className="h-4 w-4" /> Download Priority Report (.xlsx)
            </>
          )}
        </Button>
      }
    >
      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2">
        {BUCKETS.map((b) => (
          <button
            key={b}
            type="button"
            onClick={() => {
              setBucket(b);
              setPage(0);
            }}
            className={cn(
              "rounded-full border px-4 py-2 text-xs font-bold transition",
              bucket === b
                ? BUCKET_META[b].tone
                : "border-border bg-card text-muted-foreground hover:border-primary/40",
            )}
          >
            {BUCKET_META[b].emoji} {BUCKET_META[b].label}
            <span className="ml-2 tabular-nums opacity-70">{counts[b].toLocaleString()}</span>
          </button>
        ))}
        <span
          className={cn(
            "ml-auto inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase",
            live ? "bg-emerald-500/10 text-emerald-600" : "bg-muted text-muted-foreground",
          )}
        >
          <Radio className={cn("h-3 w-3", live && "animate-pulse")} />
          {live ? "Live sync on" : "Connecting…"}
        </span>
      </div>

      {/* Dynamic tier date ranges */}
      <div className="mt-4 grid gap-3 rounded-2xl border border-border bg-card p-4 sm:grid-cols-2">
        <RangeField
          label="🔴 Urgent tier"
          hint="Interviews on or before the end date."
          start={ranges.urgentStart}
          end={ranges.urgentEnd}
          onStart={(v) => setRange({ urgentStart: v })}
          onEnd={(v) => setRange({ urgentEnd: v })}
        />
        <RangeField
          label="🟡 Somewhat urgent tier"
          hint="Everything after this window (plus unscheduled) lands in 🟢 Not Urgent."
          start={ranges.somewhatStart}
          end={ranges.somewhatEnd}
          onStart={(v) => setRange({ somewhatStart: v })}
          onEnd={(v) => setRange({ somewhatEnd: v })}
        />
        <div className="sm:col-span-2">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-xs"
            onClick={() => {
              setRanges(defaultRanges());
              setPage(0);
            }}
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset to defaults
          </Button>
        </div>
      </div>

      {/* Search + status filter */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, email, WhatsApp, city…"
            className="h-9 w-full rounded-xl border border-border bg-card pl-9 pr-3 text-xs font-medium focus:border-primary focus:outline-none"
          />
        </div>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(0);
          }}
          aria-label="Filter by status"
          className="h-9 rounded-xl border border-border bg-card px-3 text-xs font-medium capitalize focus:border-primary focus:outline-none"
        >
          <option value="">All statuses</option>
          {statuses.map((s) => (
            <option key={s} value={s} className="capitalize">
              {s.replace(/_/g, " ")}
            </option>
          ))}
        </select>
        {status && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setStatus("");
              setPage(0);
            }}
          >
            Clear
          </Button>
        )}
        <SortControl value={sort} onChange={setSort} label="Sort students" />
        {query.isFetching && !showSkeleton && (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Updating…
          </span>
        )}
      </div>

      {/* Virtualized table */}
      <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-card">
        <div className="min-w-[1000px]">
          <div className="grid grid-cols-[1.6fr_1.1fr_1.7fr_1.1fr_1fr_0.9fr_1fr_1fr_1fr] bg-muted/50 px-3 py-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            <span>Student</span>
            <span>Phone</span>
            <span>Email</span>
            <span>Plan</span>
            <span>Payment mode</span>
            <span>Payment date</span>
            <span>Interview date</span>
            <span>City</span>
            <span>Status</span>
          </div>

          {showSkeleton ? (
            <TableSkeleton rows={12} cols={COLS} />
          ) : rows.length === 0 ? (
            <p className="px-3 py-10 text-center text-xs text-muted-foreground">
              No students in this tier.
            </p>
          ) : (
            <div ref={scrollRef} className="h-[600px] w-full overflow-y-auto">
              <div
                className="relative w-full"
                style={{ height: `${items.length ? virtualizer.getTotalSize() : rows.length * ROW_HEIGHT}px` }}
              >
                {(items.length
                  ? items.map((v) => ({ index: v.index, size: v.size, start: v.start }))
                  : // Fallback: if virtualization reports no items (e.g. the scroll
                    // element hasn't been measured yet), render every row directly.
                    rows.map((_, index) => ({
                      index,
                      size: ROW_HEIGHT,
                      start: index * ROW_HEIGHT,
                    }))
                ).map((v) => {
                  const r = rows[v.index]!;
                  const name = r.name || "Unknown";
                  const email = r.email ?? null;
                  const phone = r.phone ?? null;
                  return (
                    <div
                      key={r.key}
                      className="absolute left-0 top-0 grid w-full grid-cols-[1.6fr_1.1fr_1.7fr_1.1fr_1fr_0.9fr_1fr_1fr_1fr] items-center border-b border-border/60 px-3 text-xs hover:bg-muted/30"
                      style={{ height: v.size, transform: `translateY(${v.start}px)` }}
                    >
                      <span className="truncate font-semibold text-foreground">{name}</span>
                      <span className="truncate">
                        {phone ? (
                          <a
                            href={buildWhatsAppUrl(
                              phone,
                              `Hi ${name}, this is the WeLivdIt team regarding your US visa interview.`,
                            )}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 font-medium text-emerald-600 hover:underline"
                          >
                            <MessageCircle className="h-3.5 w-3.5" />
                            {phone}
                          </a>
                        ) : (
                          "—"
                        )}
                      </span>
                      <span className="truncate text-muted-foreground">{email ?? "—"}</span>
                      <span className="truncate">{r.plan ?? "—"}</span>
                      <span>
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 text-[10px] font-bold",
                            r.paymentMode === "razorpay"
                              ? "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300"
                              : "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300",
                          )}
                        >
                          {r.paymentMode === "razorpay" ? "⚡ Razorpay" : "📝 Manual"}
                        </span>
                      </span>
                      <span className="tabular-nums text-muted-foreground">
                        {r.paymentDate ? r.paymentDate.slice(0, 10) : "—"}
                      </span>
                      <span className="tabular-nums font-semibold">
                        {r.interviewDate ? r.interviewDate.slice(0, 10) : "Not Scheduled"}
                      </span>
                      <span className="truncate">{r.interviewCity ?? "—"}</span>
                      <span className="truncate capitalize text-muted-foreground">
                        {r.status.replace(/_/g, " ")}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
        <p>
          Showing {total === 0 ? 0 : page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)}{" "}
          of {total.toLocaleString()} students
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <span className="tabular-nums">
            Page {page + 1} / {lastPage + 1}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= lastPage}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
          >
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </AdminShell>
  );
}

function RangeField({
  label,
  hint,
  start,
  end,
  onStart,
  onEnd,
}: {
  label: string;
  hint: string;
  start: string;
  end: string;
  onStart: (v: string) => void;
  onEnd: (v: string) => void;
}) {
  return (
    <div>
      <p className="text-xs font-bold text-foreground">{label}</p>
      <div className="mt-2 flex items-center gap-2">
        <input
          type="date"
          value={start}
          onChange={(e) => onStart(e.target.value)}
          aria-label={`${label} start date`}
          className="h-9 flex-1 rounded-xl border border-border bg-background px-2 text-xs font-medium focus:border-primary focus:outline-none"
        />
        <span className="text-xs text-muted-foreground">to</span>
        <input
          type="date"
          value={end}
          onChange={(e) => onEnd(e.target.value)}
          aria-label={`${label} end date`}
          className="h-9 flex-1 rounded-xl border border-border bg-background px-2 text-xs font-medium focus:border-primary focus:outline-none"
        />
      </div>
      <p className="mt-1.5 text-[11px] text-muted-foreground">{hint}</p>
    </div>
  );
}
